/**
 * Kanban Board Component
 *
 * Main kanban board container that displays issues in columns organized by status.
 * Supports drag-and-drop for moving issues between columns.
 *
 * @component
 * @example
 * ```tsx
 * <KanbanBoard
 *   board={boardData}
 *   issues={issuesMap}
 *   onIssueClick={(id) => setSelectedIssue(id)}
 *   onIssueMove={(id, status) => updateIssueStatus(id, status)}
 * />
 * ```
 */

import { type Issue, type Board } from '../../../api/work';
import { KanbanColumn } from './KanbanColumn';
import { useDragAndDrop } from '../shared/hooks';
import { type SortRule, type CardField } from '../shared/displayTypes';

/**
 * Props for the KanbanBoard component
 */
interface KanbanBoardProps {
  /** Board configuration including columns and metadata */
  board: Board;
  /** Map of issue IDs to issue objects (filtered for display) */
  issues: Record<string, Issue>;
  /** Full unfiltered issues map — used for progress calculation on epic/story cards */
  allIssues?: Record<string, Issue>;
  /** Callback when an issue card is clicked */
  onIssueClick: (issueId: string) => void;
  /** Callback when an issue is moved to a new status */
  onIssueMove: (issueId: string, newStatus: string) => void;
  /** Sort rules to apply within each column */
  sortRules?: SortRule[];
  /** Card fields to display on issue cards */
  visibleFields?: CardField[];
}

/**
 * Renders a kanban board with draggable issue cards organized by status columns.
 *
 * Features:
 * - Horizontal scrolling for many columns
 * - Drag and drop between columns
 * - Visual feedback during drag operations
 * - Accessible keyboard navigation
 * - Responsive card layout
 *
 * @param props - Component props
 * @returns Kanban board JSX element
 */
export function KanbanBoard({ board, issues, allIssues, onIssueClick, onIssueMove, sortRules, visibleFields }: KanbanBoardProps) {
  const {
    draggedIssueId,
    dragOverColumnId,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  } = useDragAndDrop();

  return (
    <div
      className="flex-1 overflow-x-auto kanban-scrollbar-subtle"
      style={{ backgroundColor: '#F5F1EC' }}
      role="region"
      aria-label="Kanban board"
    >
      <div
        className="flex h-full p-6 space-x-4 min-w-min"
        role="list"
        aria-label="Kanban columns"
      >
        {board.columns.map((column) => (
          <KanbanColumn
            key={column.id}
            column={column}
            issues={issues}
            allIssues={allIssues || issues}
            onIssueClick={onIssueClick}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={(columnId) => handleDrop(columnId, onIssueMove)}
            draggedIssueId={draggedIssueId}
            isDragOver={dragOverColumnId === column.id}
            sortRules={sortRules}
            visibleFields={visibleFields}
          />
        ))}
      </div>
    </div>
  );
}
